// Publicar uma coleção.
//
// A ação existia só na API: `POST /api/colecoes/{id}/publicar`. Quem operava
// tinha de montar a requisição à mão — e publicar é a operação que **muda o
// que o cliente vê**, a única do back office com efeito externo imediato.
//
// O período de graça ganha campo próprio porque a Q6 o tornou uma escolha com
// consequência: é ele que mantém a versão anterior servível, e **só há para
// onde voltar se ele existir** — publicar sem graça descarta o caminho da
// reversão. Omitir dá 30 dias; zero não é oferecido.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { enviar, obter, type Colecao, type ResultadoPublicacao, type Versao } from "../api.js";
import { Carregando, Falha, Gaveta } from "../pecas/basicos.js";

/** Opções de prazo. 30 é o padrão da Q6; os outros cobrem os casos vizinhos. */
const PRAZOS = [7, 15, 30, 60, 90];

export function Publicar({
  colecao,
  avisar,
  aoFechar,
}: {
  colecao: Colecao;
  avisar: (texto: string, erro?: boolean) => void;
  aoFechar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [versaoId, setVersaoId] = useState("");
  const [dias, setDias] = useState(30);

  const versoes = useQuery({
    queryKey: ["versoes", colecao.camada_id],
    queryFn: () => obter<Versao[]>(`/camadas/${colecao.camada_id}/versoes`),
  });

  const publicar = useMutation({
    mutationFn: () => {
      // A data é calculada aqui e mandada explícita: o servidor tem o próprio
      // padrão, mas quem publica escolheu um prazo na tela e precisa receber
      // de volta exatamente o que escolheu.
      const ate = new Date(Date.now() + dias * 24 * 60 * 60 * 1000).toISOString();
      return enviar<ResultadoPublicacao>(`/colecoes/${colecao.id_publico}/publicar`, {
        versao_id: versaoId,
        graca_ate: ate,
      });
    },
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: ["colecoes"] });
      avisar(
        r.gracaAte === null
          ? "Publicada. Primeira publicação: não há versão anterior a preservar."
          : `Publicada. A anterior continua servível por ${dias.toString()} dias.`,
      );
      aoFechar();
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  // Só versão SELADA é publicável — a trava é do banco, e oferecer as outras
  // seria convidar a um erro que o banco recusa depois.
  const publicaveis = (versoes.data ?? []).filter((v) => v.selada_em !== null);

  return (
    <Gaveta
      titulo={`Publicar ${colecao.id_publico}`}
      subtitulo={colecao.titulo}
      aoFechar={aoFechar}
    >
      {versoes.isLoading ? <Carregando /> : null}
      {versoes.error ? <Falha erro={versoes.error} /> : null}

      {versoes.isSuccess && publicaveis.length === 0 ? (
        <p className="nota-principio">
          Nenhuma versão selada nesta camada. Promova na tela de Camadas — promover monta a versão
          com o que foi validado e a sela.
        </p>
      ) : null}

      {publicaveis.length > 0 ? (
        <>
          <div className="campo">
            <label htmlFor="versao">Versão a publicar</label>
            <select id="versao" value={versaoId} onChange={(e) => setVersaoId(e.target.value)}>
              <option value="">Escolha…</option>
              {publicaveis.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.numero} — {v.total_feicoes} feições
                  {v.numero === colecao.versao_vigente ? " (vigente)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="graca">Período de graça</label>
            <select id="graca" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
              {PRAZOS.map((d) => (
                <option key={d} value={d}>
                  {d} dias{d === 30 ? " (padrão)" : ""}
                </option>
              ))}
            </select>
          </div>

          <p className="nota-principio">
            Durante a graça, a versão anterior continua servível para quem já a estava lendo — e é
            ela que torna a reversão possível. Não existe publicar sem graça: quem não escolhe
            recebe 30 dias.
          </p>

          <div className="linha-botoes">
            <button
              className="btn btn-primario"
              disabled={versaoId === "" || publicar.isPending}
              onClick={() => publicar.mutate()}
            >
              {publicar.isPending ? "Publicando…" : "Publicar"}
            </button>
          </div>
        </>
      ) : null}
    </Gaveta>
  );
}
